import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface OutreachFormProps {
  form: UseFormReturn<any>
  isView?: boolean
}

export function OutreachForm({ form, isView }: OutreachFormProps) {
  const [legalInfoEnabled, setLegalInfoEnabled] = useState(false)
  const [communityLinkageEnabled, setCommunityLinkageEnabled] = useState(false)
  const [communityOutreachEnabled, setCommunityOutreachEnabled] = useState(false)

  return (
    <div className="space-y-6">
      {/* Legal Information Dissemination */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={legalInfoEnabled}
            onCheckedChange={(checked) => setLegalInfoEnabled(checked as boolean)}
            disabled={isView}
            id="legal-info"
          />
          <label
            htmlFor="legal-info"
            className="text-base font-bold cursor-pointer"
          >
            Legal Information Dissemination
          </label>
        </div>

        {legalInfoEnabled && (
          <div className="pl-6 space-y-4 border-l-2 border-primary/20">
            <FormField
              control={form.control}
              name="Topics Discussed"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Topics Discussed *</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isView} placeholder="Enter topics discussed" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Activity Undertaken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity Undertaken *</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isView} placeholder="Enter activity undertaken" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      disabled={isView}
                      placeholder="Enter remarks..."
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      {/* Community Linkage */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={communityLinkageEnabled}
            onCheckedChange={(checked) => setCommunityLinkageEnabled(checked as boolean)}
            disabled={isView}
            id="community-linkage"
          />
          <label
            htmlFor="community-linkage"
            className="text-base font-bold cursor-pointer"
          >
            Community Linkage
          </label>
        </div>

        {communityLinkageEnabled && (
          <div className="pl-6 border-l-2 border-primary/20">
            <FormField
              control={form.control}
              name="Coordinating Office"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Coordinating Office *</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isView} placeholder="Enter coordinating office" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      {/* Community Outreach */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={communityOutreachEnabled}
            onCheckedChange={(checked) => setCommunityOutreachEnabled(checked as boolean)}
            disabled={isView}
            id="community-outreach"
          />
          <label
            htmlFor="community-outreach"
            className="text-base font-bold cursor-pointer"
          >
            Community Outreach
          </label>
        </div>

        {communityOutreachEnabled && (
          <div className="pl-6 border-l-2 border-primary/20">
            <FormField
              control={form.control}
              name="Activity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isView}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select activity" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="LEGAL COUNSELLING">LEGAL COUNSELLING</SelectItem>
                      <SelectItem value="LEGAL DOCUMENTATION">LEGAL DOCUMENTATION</SelectItem>
                      <SelectItem value="OATHS ADMINISTERED">OATHS ADMINISTERED</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      {/* Date of Activity - Always Required */}
      <FormField
        control={form.control}
        name="Date of Activity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date of Activity *</FormLabel>
            <FormControl>
              <Input {...field} type="date" disabled={isView} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
